import { useState } from "react";
import { fieldRules } from "@/lib/validation";
import { Link } from "react-router-dom";
import { Truck, Store, Globe, Package, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { api, eur, formatApiError } from "@/lib/api";
import { ORDER_STATUS, EU_COUNTRIES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { FaqList } from "@/components/shop/HomeSections";
import { useShippingConfig, euro, promoText, freeOver } from "@/lib/shipping";

export const PageHead = ({ eyebrow, title, text, testid }) => (
  <div>
    <p className="font-display text-sm uppercase tracking-[0.2em] text-amber-600">{eyebrow}</p>
    <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-5xl" data-testid={testid}>{title}</h1>
    {text && <p className="mt-3 max-w-2xl text-stone-600">{text}</p>}
  </div>
);
const Wrap = ({ children }) => <div className="mx-auto max-w-[1000px] px-4 py-8 sm:px-6 sm:py-12">{children}</div>;

export function FaqPage() {
  return (
    <Wrap>
      <PageHead eyebrow="Assistenza" title="Domande frequenti" testid="faq-title" text="Non trovi la risposta? Scrivici a info@cardsource.eu." />
      <div className="mt-8"><FaqList prefix="faq-page" triggerClass="" /></div>
    </Wrap>
  );
}

const shipRows = (c) => [
  [Truck, "Italia", [euro(c.italy_fee), promoText(c, "italy")].filter(Boolean).join(" · "), "Consegna in 24/72 ore lavorative con corriere tracciato."],
  [Globe, "Europa (UE)", [euro(c.eu_fee), promoText(c, "eu")].filter(Boolean).join(" · "), "Consegna in 3/7 giorni lavorativi in tutti i paesi dell'Unione Europea."],
  [Store, "Ritiro in negozio", "Gratuito", "Ti avvisiamo via email quando l'ordine è pronto per il ritiro."],
  [Package, "Contrassegno", `+${euro(c.cod_fee)} · solo Italia`, "Paghi in contanti al corriere alla consegna."],
];

const RETURNS = [
  ["Diritto di recesso: 14 giorni", "Puoi restituire i prodotti entro 14 giorni dalla consegna, senza dover dare spiegazioni. Scrivici a help.order@cardsource.eu indicando il numero d'ordine: ti rispondiamo con le istruzioni."],
  ["Condizioni del prodotto", "I prodotti sigillati devono essere integri, con il cellophane originale. Le carte singole devono tornare nella stessa condizione in cui le hai ricevute, nella loro protezione (sleeve e toploader)."],
  ["Spese di reso", "La spedizione di ritorno è a carico tuo. Ti consigliamo un corriere tracciato e assicurato: fino a quando il pacco non arriva a noi la responsabilità è del mittente."],
  ["Rimborso", "Entro 14 giorni dal ricevimento del reso ti rimborsiamo l'importo pagato, con lo stesso metodo di pagamento. Il costo della spedizione standard dell'ordine è incluso nel rimborso se restituisci tutto l'ordine."],
  ["Preordini", "Per i preordini non accettiamo resi, salvo il diritto di recesso che la legge riconosce ai consumatori (vedi Condizioni di vendita) e i prodotti difettosi o diversi da quelli ordinati: in questi casi ti sostituiamo il prodotto o ti rimborsiamo per intero."],
  ["Prodotto difettoso o sbagliato", "Se ricevi un prodotto danneggiato, difettoso o diverso dall'ordine, scrivici entro 14 giorni con qualche foto: le spese di ritorno sono a carico nostro."],
];

export function ShippingPage() {
  const cfg = useShippingConfig();
  const SHIP = shipRows(cfg);
  const it = freeOver(cfg, "italy");
  const eu = freeOver(cfg, "eu");
  return (
    <Wrap>
      <PageHead eyebrow="Spedizioni e resi" title="Spedizioni e resi" testid="shipping-title" text="Spediamo ogni ordine con imballaggio rinforzato: carte singole in sleeve + toploader, prodotti sigillati protetti con pluriball e scatola rigida." />
      <p className="mt-6 rounded-2xl bg-amber-500/15 p-4 font-semibold text-stone-800" data-testid="shipping-threshold-sentence">
        {it || eu ? `Spedizione gratuita ${[it && `in Italia sopra ${euro(it)} d'ordine`, eu && `in Europa sopra ${euro(eu)} d'ordine`].filter(Boolean).join(" e ")}: sotto questa cifra paghi solo la tariffa indicata qui sotto.` : "La tariffa di spedizione è indicata qui sotto e calcolata in automatico al checkout."}
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="shipping-options">
        {SHIP.map(([Icon, t, price, d]) => (
          <div key={t} className="rounded-2xl border border-black/10 bg-white p-6">
            <Icon className="h-7 w-7 text-amber-600" />
            <p className="mt-3 font-display text-xl">{t}</p>
            <p className="font-semibold text-violet-500">{price}</p>
            <p className="mt-1 text-sm text-stone-600">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-2xl border border-black/10 bg-white p-6 text-sm text-stone-600">
        <p className="font-display text-lg text-stone-900">Paesi serviti</p>
        <p className="mt-2">{EU_COUNTRIES.join(", ")}.</p>
        <p className="mt-4">Gli ordini pagati entro le 13:00 partono in giornata (lun-ven). Riceverai il numero di tracking via email e potrai seguirlo nella pagina <Link to="/traccia" className="font-semibold text-amber-600 underline">Traccia la tua spedizione</Link>.</p>
      </div>
      <h2 className="mt-12 font-display text-2xl font-bold" id="resi" data-testid="returns-title">Resi e rimborsi</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="returns-policy">
        {RETURNS.map(([t, d]) => (
          <div key={t} className="rounded-2xl border border-black/10 bg-white p-5">
            <p className="font-display text-lg">{t}</p>
            <p className="mt-1 text-sm text-stone-600">{d}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-stone-500">Il testo completo è nelle <Link to="/condizioni-di-vendita" className="underline">Condizioni di vendita</Link>. Il recesso vale per gli acquisti dei consumatori, non per gli ordini B2B.</p>
    </Wrap>
  );
}

export function TrackPage() {
  const [f, setF] = useState({ number: "", email: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setRes((await api.get("/orders-track", { params: f })).data);
    } catch (err) {
      setRes(null);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const inp = "h-12 w-full rounded-xl border border-black/15 bg-white px-4 outline-none focus:border-amber-500";
  return (
    <Wrap>
      <PageHead eyebrow="Spedizioni" title="Traccia la tua spedizione" testid="track-title" text="Inserisci il numero d'ordine (es. CS-123456) e l'email usata per l'acquisto." />
      <form onSubmit={submit} className="mt-8 grid gap-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="track-form">
        <input required value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="Numero ordine" className={inp} data-testid="track-number" />
        <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={inp} data-testid="track-email" />
        <button disabled={busy} className="sticker-btn flex h-12 items-center justify-center gap-2 rounded-full bg-black px-6 text-white" data-testid="track-submit"><Search className="h-4 w-4" />Traccia</button>
      </form>
      {res && (
        <div className="mt-8 rounded-2xl border-2 border-black bg-white p-6 shadow-[5px_5px_0_#000]" data-testid="track-result">
          <p className="font-display text-xl">Ordine {res.order_number}</p>
          <p className="mt-1 text-sm text-stone-600">Effettuato il {new Date(res.created_at).toLocaleDateString("it-IT")} · Totale {eur(res.total)}</p>
          <p className="mt-4 text-sm">Stato: <span className="font-semibold" data-testid="track-status">{ORDER_STATUS[res.status]?.label || res.status}</span></p>
          {res.delivery === "ritiro" ? <p className="mt-2 text-sm text-stone-600">Ritiro in negozio: ti avviseremo quando sarà pronto.</p> : res.tracking_number ? (
            <p className="mt-2 text-sm" data-testid="track-tracking">Corriere <b>{res.carrier}</b> · Tracking <span className="font-mono">{res.tracking_number}</span>
              {res.tracking_url && <a href={res.tracking_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 font-semibold text-amber-600 underline">Segui la spedizione<ExternalLink className="h-3.5 w-3.5" /></a>}</p>
          ) : <p className="mt-2 text-sm text-stone-600" data-testid="track-pending">Il numero di tracking sarà disponibile appena l'ordine verrà spedito.</p>}
        </div>
      )}
    </Wrap>
  );
}

const B2B_POINTS = [
  ["Listino dedicato", "Prezzi all'ingrosso riservati su display, booster box e case sigillati."],
  ["Preordini garantiti", "Allocazioni sulle nuove uscite di tutti i giochi che trattiamo."],
  ["Spedizioni rapide", "Spedizioni assicurate in Italia e in Europa, con consegna in 24/72 ore."],
  ["Referente dedicato", "Un contatto diretto per ordini, disponibilità e fatturazione."],
];

export function B2BPage() {
  const { user } = useAuth();
  const empty = { company: "", vat: "", contact_name: "", email: "", phone: "", city: "", country: "Italia", message: "", address: "", zip: "", province: "", sdi: "", pec: "" };
  const [f, setF] = useState(empty);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/b2b-requests", Object.fromEntries(Object.entries(f).map(([k, v]) => [k, ["address", "zip", "province", "sdi", "pec"].includes(k) && !v.trim() ? null : v])));
      setSent(true);
      setF(empty);
      toast.success("Richiesta inviata! Ti ricontatteremo a breve.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const inp = "h-11 w-full rounded-xl border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500";
  const fields = [["company", "Ragione sociale"], ["vat", "Partita IVA"], ["contact_name", "Referente"], ["phone", "Telefono"], ["city", "Città"]];
  if (user?.account_type === "b2b")
    return (
      <div className="mx-auto max-w-[900px] px-4 py-12 sm:px-6" data-testid="b2b-active">
        <PageHead eyebrow="Area B2B" title={`Benvenuto, ${user.b2b?.company || user.name}`} testid="b2b-title" text="Il tuo accesso B2B è attivo: nel catalogo vedi il listino riservato, con prezzi IVA esclusa. Puoi pagare con bonifico o in negozio al ritiro." />
        <Link to="/catalogo" className="mt-8 inline-block rounded-full bg-amber-500 px-6 py-3 font-bold text-black" data-testid="b2b-go-catalog">Vai al listino B2B →</Link>
      </div>
    );
  const pending = sent || user?.b2b_pending;
  return (
    <div className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6 sm:py-12">
      <PageHead eyebrow="Area B2B" title="Forniture per negozi" testid="b2b-title" text="Sei un negozio o un'associazione? Rivendiamo all'ingrosso prodotti sigillati TCG. Richiedi l'accesso al listino riservato." />
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_460px]">
        <div className="grid h-fit gap-4 sm:grid-cols-2">
          {B2B_POINTS.map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-black/10 bg-white p-5"><p className="font-display text-lg">{t}</p><p className="mt-1 text-sm text-stone-600">{d}</p></div>
          ))}
          <p className="text-sm text-stone-600 sm:col-span-2">Preferisci l'email? Scrivi a <a href="mailto:b2b@cardsource.eu" className="font-semibold text-amber-600 underline">b2b@cardsource.eu</a></p>
        </div>
        {!user ? (
          <div className="h-fit space-y-4 rounded-2xl border-2 border-black bg-white p-6 shadow-[6px_6px_0_#000]" data-testid="b2b-register-cta">
            <p className="font-display text-xl font-bold">Registra la tua azienda</p>
            <p className="text-sm text-stone-600">Crea l'account scegliendo “Azienda / P.IVA”: inserisci ragione sociale, Partita IVA e dati di fatturazione elettronica. Dopo la nostra verifica si attiva il listino riservato.</p>
            <Link to="/accedi?tab=registrati&tipo=azienda&next=/b2b" className="sticker-btn block h-12 rounded-full bg-black text-center leading-[3rem] text-white" data-testid="b2b-register-link">Registra l'azienda</Link>
            <p className="text-xs text-stone-500">Hai già un account? <Link to="/accedi?next=/b2b" className="text-cyan-700 underline">Accedi</Link> e richiedi l'accesso B2B da qui.</p>
          </div>
        ) : pending ? (
          <div className="h-fit rounded-2xl border-2 border-black bg-white p-6 shadow-[6px_6px_0_#000]" data-testid="b2b-pending">
            <p className="font-display text-xl font-bold">Richiesta in verifica</p>
            <p className="mt-2 text-sm text-stone-600">Stiamo controllando i dati della tua azienda. Riceverai un'email a {user.email} appena l'accesso B2B sarà attivo; nel frattempo puoi acquistare come cliente privato.</p>
          </div>
        ) : (
        <form onSubmit={submit} className="grid gap-3 rounded-2xl border-2 border-black bg-white p-6 shadow-[6px_6px_0_#000] sm:grid-cols-2" data-testid="b2b-form">
          {fields.map(([k, l, t]) => <input key={k} required type={t || "text"} placeholder={l} value={f[k]} onChange={set(k)} className={inp} {...fieldRules(k, f.country)} data-testid={`b2b-${k}`} />)}
          <select value={f.country} onChange={set("country")} className={`${inp} sm:col-span-2`} data-testid="b2b-country">{EU_COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select>
          <p className="text-xs text-stone-500 sm:col-span-2">Dati per la fattura elettronica (puoi completarli anche dopo l'approvazione)</p>
          <input placeholder="Indirizzo sede legale" value={f.address} onChange={set("address")} className={`${inp} sm:col-span-2`} {...fieldRules("address", f.country)} data-testid="b2b-address" />
          <input placeholder="CAP" value={f.zip} onChange={set("zip")} className={inp} {...fieldRules("zip", f.country)} data-testid="b2b-zip" />
          <input placeholder="Provincia (sigla)" maxLength={2} value={f.province} onChange={set("province")} className={inp} {...fieldRules("province", f.country)} data-testid="b2b-province" />
          <input placeholder="Codice SDI (7 caratteri)" maxLength={7} value={f.sdi} onChange={set("sdi")} className={inp} data-testid="b2b-sdi" />
          <input placeholder="oppure PEC" type="email" value={f.pec} onChange={set("pec")} className={inp} data-testid="b2b-pec" />
          <textarea rows={3} placeholder="Giochi e prodotti di interesse, volumi indicativi..." value={f.message} onChange={set("message")} className={`${inp} h-auto py-2 sm:col-span-2`} data-testid="b2b-message" />
          <button className="sticker-btn h-12 rounded-full bg-black text-white sm:col-span-2" data-testid="b2b-submit">Richiedi accesso B2B</button>
        </form>
        )}
      </div>
    </div>
  );
}
